import { ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators, AbstractControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CardStatus, CardConfiguration, Icon } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { DocumentViewerComponent } from 'src/app/global/components/common/document-viewer/modal-image.component';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { CardActions, UserCard, UserRecordTransformedUser } from 'src/app/user/user.model';
import { UserFamilyAndCardDialogComponent } from '../../dialogs/user-family-and-card-dialog/user-family-and-card-dialog.component';
import { UserRequestDenyDialogComponent } from '../../dialogs/user-request-deny-dialog/user-request-deny-dialog.component';
import { LanguageConfiguration } from 'src/app/global/global.model';
import { languageSelector, organisationConfigurationSelector } from 'src/app/global/store/global.selectors';

export interface UserCardModified extends UserCard {
    proofOfIdentity: string[];
}

export interface UserCardRequestFormData {
    user: UserRecordTransformedUser;
    card: UserCardModified;
    action: CardActions;
    isConnected?: boolean;
}

@Component({
    selector: 'app-user-card-not-connected-form',
    templateUrl: './user-card-not-connected-form.component.html',
    styleUrls: ['./user-card-not-connected-form.component.scss'],
})
export class UserCardNotConnectedFormComponent implements OnInit {
    public destroy$ = new Subject();

    Icon = Icon;

    CardStatus = CardStatus;

    CardAction = CardActions;

    public requestCardForm: FormGroup;

    public backgroundCheck: any;

    public loading = false;

    public discrepancies: any;

    cardConfig: CardConfiguration;

    currentLang: string;

    orgLang: LanguageConfiguration;

    isSubmitted = false;

    hideExpDate = false;

    tomorrow = new Date();

    invalidCardNumber = false;

    invalidExpDate = false;

    @Input() data: UserCardRequestFormData;

    public form = new FormGroup({
        expDate: new FormControl('', [Validators.required, this.validateDateIsInFuture]),
        cardNumber: new FormControl('', []),
    });

    get cardNumber() {
        return this.form.get('cardNumber');
    }

    get subType() {
        return this.requestCardForm?.get('subType');
    }

    get expDate(): AbstractControl {
        return this.form.get('expDate');
    }

    get cardLabel(): string {
        return this.cardConfig?.identity?.label[this.currentLang];
    }

    get cardIcon(): string {
        return this.cardConfig?.identity?.icon || Icon.MY_CARD;
    }

    public validateDateIsInFuture(c: FormControl): { [key: string]: { [key: string]: boolean } } {
        if (c.value) {
            const today = new Date().toISOString();
            return new Date(c.value).toISOString() > today ? null : { validateDateIsInFutur: { valid: false } };
        }
        return null;
    }

    constructor(
        private formBuilder: FormBuilder,
        private translateService: TranslateService,
        private service: UserService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<UserFamilyAndCardDialogComponent>,
        private store: Store<State>,
    ) {}

    approveLoading = false;

    denyLoading = false;

    public subTypes = [];

    ngOnInit(): void {
        this.loading = true;
        this.tomorrow.setDate(this.tomorrow.getDate() + 1);

        combineLatest([
            this.store.select(languageSelector),
            this.store.select(organisationConfigurationSelector),
            this.service.cardAddressExists(this.data.card._id),
            this.service.cardDiscrepancies(this.data.card._id, this.data.user.id),
        ])
            .pipe(
                map(([lang, orgConfig, cardAddressExists, discrepancies]) => {
                    this.backgroundCheck = cardAddressExists?.backgroundCheck;
                    this.discrepancies = discrepancies;
                    this.currentLang = lang;
                    this.orgLang = orgConfig.lang;
                    this.cardConfig = orgConfig?.support?.cards?.find((c) => c.type === this.data.card.cardType);
                    if (this.cardConfig?.availableSubTypes?.length > 0) {
                        this.requestCardForm = this.formBuilder.group({ subType: new FormControl('', [Validators.required]) }, { updateOn: 'blur' });

                        const subTypes = [];
                        this.cardConfig?.availableSubTypes.forEach((availableSubType) => {
                            const subType = orgConfig?.subTypes?.find((s) => s.tag === availableSubType);
                            subTypes.push({
                                label: subType?.name?.[lang],
                                value: subType.tag,
                            });
                        });
                        this.subTypes = subTypes;
                    }

                    this.loading = false;
                }),
            )
            .subscribe();

        this.service.cardAddressExists(this.data.card._id).subscribe((i) => {
            this.backgroundCheck = i;
        });

        if (this.data.action == CardActions.SUBSCRIBE) {
            this.cardNumber.addValidators(Validators.required);
        }
    }

    closeDialog(v?: boolean): void {
        if (this.dialogRef?.close) {
            this.dialogRef.close(v);
        }
    }

    handleApprove(): void {
        this.isSubmitted = true;
        this.invalidCardNumber = !this.form.controls['cardNumber'].valid;
        this.invalidExpDate = !this.form.controls['expDate'].valid;
        if (this.form.valid && this.requestCardForm?.valid) {
            if (this.data.action === CardActions.SUBSCRIBE) {
                this.approveCardRequest();
            }
            if (this.data.action === CardActions.RENEW) {
                this.approveCardRenewal();
            }
            if (this.data.action === CardActions.LINK) {
                this.approveLinkCardRequest();
            }
        }
    }

    handleDeny(): void {
        this.closeDialog(true);
        let langLabel = this.translateService.instant(`common.langs.${this.orgLang.default}`);
        if (this.data.user.preferences?.language && this.orgLang.available.includes(this.data.user.preferences?.language)) {
            langLabel = this.translateService.instant(`common.langs.${this.data.user.preferences.language}`);
        }
        const locale = this.currentLang || this.orgLang.default;
        const denialReasons = (this.cardConfig as any)?.denialReasons?.[locale] || [];
        const dialog = this.dialog.open(UserRequestDenyDialogComponent, {
            autoFocus: false,
            width: '680px',
            panelClass: '',
            data: {
                title: this.translateService.instant('pages.userRecords.card.denyCardDialog.header'),
                body: {
                    label: this.translateService.instant('pages.userRecords.card.denyCardDialog.label', { lang: langLabel }),
                    placeholder: this.translateService.instant('pages.userRecords.card.denyCardDialog.placeholder'),
                    error: this.translateService.instant('pages.userRecords.card.denyCardDialog.form.errors.reason'),
                    button: this.translateService.instant('pages.userRecords.card.denyCardDialog.button.submit'),
                    presetTab: this.translateService.instant('pages.userRecords.card.denyCardDialog.presetTab'),
                    customTab: this.translateService.instant('pages.userRecords.card.denyCardDialog.customTab'),
                },
                denialReasons,
            },
        });

        dialog.componentInstance.onSubmit.subscribe((reason) => {
            dialog.componentInstance.dialogRef.close();
            if (this.data.action === CardActions.SUBSCRIBE) {
                this.denyCardRequest(reason);
            }
            if (this.data.action === CardActions.RENEW) {
                this.denyCardRenewal(reason);
            }
            if (this.data.action === CardActions.LINK) {
                this.denyCardRequest(reason);
            }
        });
    }

    approveCardRenewal(): void {
        this.approveLoading = true;
        this.service
            .approveNotConnectedUserCardRenewal(this.data.user.id, this.data.card._id, this.requestCardForm?.value?.subType?.value, this.form.get('expDate').value, this.data.card.number)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    this.approveLoading = false;
                    this.messageService.success(
                        this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approveRenewal.success', {
                            firstName: this.data.user.firstName,
                            lastName: this.data.user.lastName,
                        }),
                        undefined,
                        { enableHtml: true },
                    );
                    this.closeDialog(true);
                },
                (_error) => {
                    this.approveLoading = false;
                    this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approveRenewal.error'));
                },
            );
    }

    denyCardRenewal(reason: string): void {
        this.denyLoading = true;
        this.service
            .denyUserCardRenewal(this.data.user.id, this.data.card._id, reason)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    this.denyLoading = false;
                    this.messageService.success(
                        this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.denyRenewal.success', {
                            firstName: this.data.user.firstName,
                            lastName: this.data.user.lastName,
                        }),
                        undefined,
                        { enableHtml: true },
                    );
                    this.closeDialog(true);
                },
                (_error) => {
                    this.approveLoading = false;
                    this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.denyRenewal.error'));
                },
            );
    }

    approveCardRequest(): void {
        this.approveLoading = true;

        this.service
            .approveNotConnectedUserCard(this.data.user.id, this.data.card._id, this.requestCardForm?.value?.subType?.value, this.form.get('expDate').value, this.form.get('cardNumber').value)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    this.approveLoading = false;
                    this.messageService.success(
                        this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approve.success', {
                            firstName: this.data.user.firstName,
                            lastName: this.data.user.lastName,
                        }),
                        undefined,
                        { enableHtml: true },
                    );
                    this.closeDialog(true);
                },
                (_error) => {
                    this.approveLoading = false;
                    this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approve.error'));
                },
            );
    }

    approveLinkCardRequest(): void {
        this.approveLoading = true;
        this.service
            .approveNotConnectedUserCard(this.data.user.id, this.data.card._id, this.requestCardForm?.value?.subType?.value, this.form.get('expDate').value, this.data.card.number)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    this.approveLoading = false;
                    this.messageService.success(
                        this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approve.success', {
                            firstName: this.data.user.firstName,
                            lastName: this.data.user.lastName,
                        }),
                        undefined,
                        { enableHtml: true },
                    );
                    this.closeDialog(true);
                },
                (_error) => {
                    this.approveLoading = false;
                    this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.approve.error'));
                },
            );
    }

    denyCardRequest(reason: string): void {
        this.denyLoading = true;
        this.service
            .denyUserCard(this.data.user.id, this.data.card._id, reason)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    this.denyLoading = false;
                    this.messageService.success(
                        this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.deny.success', {
                            firstName: this.data.user.firstName,
                            lastName: this.data.user.lastName,
                        }),
                        undefined,
                        { enableHtml: true },
                    );
                    this.closeDialog(true);
                },
                (_error) => {
                    this.denyLoading = false;
                    this.messageService.error(this.translateService.instant('pages.userRecords.card.requestDetailsDialog.message.deny.error'));
                },
            );
    }

    public validateInfoStep() {
        return this.form.controls['cardNumber'].valid && this.form.controls['expDate'].valid;
    }

    viewDocument(url: string): void {
        this.dialog.open(DocumentViewerComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            data: { src: url },
            panelClass: 'document-viewer-dialog',
        });
    }

    formatDate(date: string): string {
        if (!date) {
            return '';
        }
        // Extract date portion from ISO string to avoid timezone conversion issues
        let dateString = date;
        if (date.includes('T')) {
            dateString = date.split('T')[0];
        }
        // Parse date components and create Date in local timezone
        const [year, month, day] = dateString.split('-').map(Number);
        const tempDate = new Date(year, month - 1, day);
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Intl.DateTimeFormat(`${this.translateService.currentLang}-CA`, options).format(tempDate);
    }

    urlIsImage(url: string): boolean {
        return ['.png', '.jpg', '.jpeg', '.gif'].indexOf(url.substring(url.lastIndexOf('.'))) > -1;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
